import type { GamificationActionContext, GmRewardAction } from '@/components/features/gamification/model/types/gamificationActions';
import { show as showIntercom } from '@intercom/messenger-js-sdk';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { resolveGamificationActionTarget } from '@/components/features/gamification/model/utils/getGamificationActionTarget';
import { useUnclaimedGamificationRewardsCount } from '@/components/features/gamification/modules/rewards/hooks/useUnclaimedGamificationRewardsCount';
import Toast from '@/components/ui/Toast/toast';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { useProgressRouterIntel } from '@/hooks/useProgressRouter';
import openModal from '@/modals/utils/openModal';
import { useUserMeQuery } from '@/queries/user/user.queries';
import { getStatusUpRewardStatusId, getUnclaimedStatusUpRewards } from '../utils/statusUpRewards';
import { useManageStatusUpActions } from './useManageStatusUpActions';

export const useOpenGamificationRewardAction = () => {
  const t = useTranslations('gamification.scratch-card-statuses');
  const isMobile = useMediaQuery('(max-width: 768px)');
  const queryClient = useQueryClient();
  const router = useProgressRouterIntel();
  const { data: user } = useUserMeQuery();
  // Grouped rewards are the lightweight source of truth for badges and for deciding
  // whether the reward should enter the claim flow or just show the cards.
  const { levelUpRewards, statusUpRewards } = useUnclaimedGamificationRewardsCount();
  const { handleScratchAction: handleLevelUpScratchAction } = useManageStatusUpActions('level-up');

  return useCallback(async (action?: GmRewardAction, context: GamificationActionContext = { surface: 'statuses' }) => {
    if (!action) {
      return;
    }

    if (action === 'open-support') {
      showIntercom();
      return;
    }

    if (!user?.id) {
      return;
    }

    // Status-up has two flows:
    // 1. Unclaimed reward exists: show congratulations first, then its CTA continues to scratch-card claim.
    // 2. No unclaimed reward: keep the original statuses slider/card view as an informational fallback.
    if (action === 'status-up-card' && statusUpRewards > 0) {
      let rewards: Awaited<ReturnType<typeof getUnclaimedStatusUpRewards>>;
      try {
        rewards = await getUnclaimedStatusUpRewards(queryClient);
      } catch {
        Toast({
          type: 'error',
          title: t('rewards-load-error-title'),
          description: t('rewards-load-error-description'),
        });
        return;
      }

      const statusId = rewards[0] && getStatusUpRewardStatusId(rewards[0]);

      if (statusId) {
        openModal('gm-congratulations-status-up', { statusId });
        return;
      }
    }

    if (action === 'level-up-card' && levelUpRewards > 0) {
      handleLevelUpScratchAction();
      return;
    }

    const target = resolveGamificationActionTarget(action, context);
    if (!target) {
      return;
    }

    if (isMobile && target.mobileHref) {
      router.push(`${target.mobileHref}?userId=${user.id}`);
      return;
    }

    if (target.kind === 'modal') {
      openModal(target.modalName);
      return;
    }

    router.push(target.href);
  }, [handleLevelUpScratchAction, isMobile, levelUpRewards, queryClient, router, statusUpRewards, t, user?.id]);
};
